// DarkModeClass.tsx
import { useEffect } from 'react';
import Toggle from '../components/Toggle';
import { useSelector } from 'react-redux';
import { appDispatch, RootState } from '../store/store';
import { changeThemeMode } from '../store/app/appSlice';
import { useTranslation } from 'react-i18next';
import { black } from 'utils/style/colors';

interface IProps {
  isWebda: boolean;
}

export const toggleToDarkMode = () => {
  appDispatch(changeThemeMode({ themeMode: 'dark' }));
  document.body.classList.add('dark-mode');
};

export const toggleWebdaToLightMode = () => {
  appDispatch(changeThemeMode({ webdaThemeMode: 'light' }));
  document.body.classList.add('webda-light-mode');
};

export const toggleWebdaToDarkMode = () => {
  appDispatch(changeThemeMode({ webdaThemeMode: 'dark' }));
  document.body.classList.add('webda-dark-mode');
};

export const toggleDOMToLightMode = () => {
  document.body.classList.remove('dark-mode');
};

const DarkModeClass: React.FC<IProps> = ({ isWebda = false }) => {
  const { t } = useTranslation('general');

  const isDarkMode =
    useSelector((state: RootState) => state.app.UI.themeMode) === 'dark';
  const isWebdaDarkMode =
    useSelector((state: RootState) => state.app.UI.webdaThemeMode) === 'dark';

  useEffect(() => {
    if (!isWebda) {
      const darkModeLocalStorage = localStorage.getItem('dark-mode') === 'true';
      if (isDarkMode || darkModeLocalStorage) {
        document.body.classList.add('dark-mode');
      }

      if (darkModeLocalStorage && !isDarkMode) {
        appDispatch(changeThemeMode({ themeMode: 'dark' }));
      }
    } else {
      const webdaDarkModeLocalStorage =
        localStorage.getItem('webda-dark-mode') === 'true';
      if (webdaDarkModeLocalStorage) {
        document.body.classList.add('webda-dark-mode');
      } else {
        document.body.classList.add('webda-light-mode');
      }

      if (webdaDarkModeLocalStorage && !isWebdaDarkMode) {
        appDispatch(changeThemeMode({ webdaThemeMode: 'dark' }));
      }
    }
  }, []);

  const toggleDarkMode = () => {
    if (!isWebda) {
      const newTheme = isDarkMode ? 'light' : 'dark';
      appDispatch(changeThemeMode({ themeMode: newTheme }));

      if (newTheme === 'dark') {
        document.body.classList.add('dark-mode');
        localStorage.setItem('dark-mode', 'true');
      } else {
        document.body.classList.remove('dark-mode');
        localStorage.setItem('dark-mode', 'false');
      }
    } else {
      const newTheme = isWebdaDarkMode ? 'light' : 'dark';
      appDispatch(changeThemeMode({ webdaThemeMode: newTheme }));

      if (newTheme === 'dark') {
        document.body.classList.remove('webda-light-mode');
        document.body.classList.add('webda-dark-mode');
        localStorage.setItem('webda-dark-mode', 'true');
      } else {
        document.body.classList.remove('webda-dark-mode');
        document.body.classList.add('webda-light-mode');
        localStorage.setItem('webda-dark-mode', 'false');
      }
    }
  };

  const themeStatus = !isWebda
    ? isDarkMode
      ? 'dark'
      : 'light'
    : isWebdaDarkMode
      ? 'dark'
      : 'light';

  return (
    <div
      style={{
        minHeight: '4rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: black,
      }}
    >
      <Toggle
        label={t(themeStatus) as string}
        name="batchAdd"
        title={t(themeStatus) as string}
        onChange={toggleDarkMode}
        checked={isWebda ? isWebdaDarkMode : isDarkMode}
        wrapperStyle={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      />
    </div>
  );
};

export default DarkModeClass;
